import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { Paragraph } from '@lyceo/lyceo-pupil-app/practise/util-models';
import { ParagraphActions } from './paragraph.actions';
import { BookActions } from '../book/book.actions';
import { flatten } from '@lyceo/shared/util';
import { CallState, LoadingState } from '@lyceo/shared/util-loading';
import { PractiseBookPageActions } from '../practise/practise.actions';
import { mapParagraph, mapParagraphSummary } from './paragraph.functions';
import { AuthActions } from '@lyceo/shared/auth/data-access';

export const PARAGRAPH_FEATURE_KEY: string = 'paragraph';

export interface ParagraphState extends EntityState<Paragraph> {
  fullyLoadedIds: string[];
  paragraphCallState: CallState;
}

export function sortByPosition(a: Paragraph, b: Paragraph): number {
  return a.position - b.position;
}

export const paragraphAdapter: EntityAdapter<Paragraph> =
  createEntityAdapter<Paragraph>({
    sortComparer: sortByPosition,
  });

export const initialParagraphState: ParagraphState =
  paragraphAdapter.getInitialState({
    fullyLoadedIds: [],
    paragraphCallState: LoadingState.INIT,
  });

const reducer: ActionReducer<ParagraphState, Action> = createReducer(
  initialParagraphState,
  on(AuthActions.logoutSuccess, (): ParagraphState => initialParagraphState),
  on(
    ParagraphActions.getParagraphByIdsSuccess,
    (state, action): ParagraphState =>
      paragraphAdapter.upsertMany(
        action.paragraphs.map((paragraphDto) => mapParagraph(paragraphDto)),
        {
          ...state,
          fullyLoadedIds: [
            ...state.fullyLoadedIds,
            ...action.paragraphs.map((paragraph) => paragraph.id),
          ],
        },
      ),
  ),
  on(
    ParagraphActions.getParagraphByIdFailure,
    (state, { error }): ParagraphState => ({
      ...state,
      paragraphCallState: { errorMsg: error.message },
    }),
  ),
  on(
    PractiseBookPageActions.init,
    (state): ParagraphState => ({
      ...state,
      paragraphCallState: LoadingState.LOADING,
    }),
  ),
  on(
    ParagraphActions.getParagraphByIdSuccess,
    (state, action): ParagraphState =>
      paragraphAdapter.upsertOne(mapParagraph(action.paragraph), {
        ...state,
        paragraphCallState: LoadingState.LOADED,
        fullyLoadedIds: [...state.fullyLoadedIds, action.paragraph.id],
      }),
  ),
  on(
    BookActions.getBookSuccess,
    (state, action): ParagraphState =>
      paragraphAdapter.upsertMany(
        flatten(
          action.book.chapters.map((chapterDto) =>
            chapterDto.paragraphs.map((paragraph) =>
              mapParagraphSummary(paragraph, action.book.id, chapterDto.id),
            ),
          ),
        ),
        {
          ...state,
        },
      ),
  ),
);

export function paragraphReducer(
  state: ParagraphState | undefined,
  action: Action,
): ParagraphState {
  return reducer(state, action);
}
